import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  Beaker,
  History,
  Layers3,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Rocket,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  assignTeamAiSkill,
  changeTeamAiSkillVersionState,
  createTeamAiSkill,
  createTeamAiSkillVersion,
  evaluateTeamAiSkillVersion,
  listTeamAiSkillEvaluations,
  listTeamAiSkills,
  removeTeamAiSkillAssignment,
  updateTeamAiSkillDraft,
} from './admin-team-ai-skills.api';
import {
  SkillAssignmentDialog,
  SkillDraftDialog,
  SkillVersionDialog,
} from './admin-team-ai-skills.dialogs';
import type {
  TeamAiSkill,
  TeamAiSkillAssignment,
  TeamAiSkillCreateInput,
  TeamAiSkillEvaluationRun,
  TeamAiSkillScope,
  TeamAiSkillVersion,
} from './admin-team-ai-skills.types';
import {
  EMPTY_SKILL_FORM,
  isTeamAiSkillImport,
  MAX_IMPORT_FILE_BYTES,
  skillStateBadgeVariant,
  describeSkillScope,
} from './admin-team-ai-skills.utils';
import {
  EvaluationEvidence,
  SkillCatalogList,
} from './admin-team-ai-skills.views';

export function AdminTeamAiSkillsWorkspace() {
  const { t } = useTranslation('admin');
  const [skills, setSkills] = useState<TeamAiSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const [evaluations, setEvaluations] = useState<TeamAiSkillEvaluationRun[]>(
    [],
  );
  const [evaluationsLoading, setEvaluationsLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [evaluationOutputTokens, setEvaluationOutputTokens] = useState(2400);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [skillDialogMode, setSkillDialogMode] = useState<'create' | 'import'>(
    'create',
  );
  const [skillForm, setSkillForm] =
    useState<TeamAiSkillCreateInput>(EMPTY_SKILL_FORM);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'edit' | 'new'>('edit');
  const [contentDraft, setContentDraft] = useState('');
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [scopeType, setScopeType] = useState<TeamAiSkillScope>('platform');
  const [scopeKey, setScopeKey] = useState('');
  const [priority, setPriority] = useState('100');
  const [retireTarget, setRetireTarget] = useState<TeamAiSkillVersion | null>(
    null,
  );
  const [removeTarget, setRemoveTarget] =
    useState<TeamAiSkillAssignment | null>(null);

  const selectedSkill = useMemo(
    () => skills.find((skill) => skill.id === selectedSkillId) || null,
    [selectedSkillId, skills],
  );
  const selectedVersion = useMemo(
    () =>
      selectedSkill?.versions.find(
        (version) => version.id === selectedVersionId,
      ) ||
      selectedSkill?.versions[0] ||
      null,
    [selectedSkill, selectedVersionId],
  );

  const loadSkills = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);
      try {
        const nextSkills = await listTeamAiSkills();
        setSkills(nextSkills);
        setSelectedSkillId((current) =>
          current && nextSkills.some((skill) => skill.id === current)
            ? current
            : nextSkills[0]?.id || null,
        );
      } catch (error) {
        console.error('Failed to load Team AI skills:', error);
        toast.error(t('teamAiSkills.errors.load'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadSkills();
  }, [loadSkills]);

  useEffect(() => {
    setSelectedVersionId((current) =>
      current &&
      selectedSkill?.versions.some((version) => version.id === current)
        ? current
        : selectedSkill?.versions[0]?.id || null,
    );
  }, [selectedSkill]);

  useEffect(() => {
    if (!selectedVersion) {
      setEvaluations([]);
      return;
    }
    let active = true;
    setEvaluationsLoading(true);
    void listTeamAiSkillEvaluations(selectedVersion.id)
      .then((runs) => {
        if (active) setEvaluations(runs);
      })
      .catch((error: unknown) => {
        if (active) {
          console.error('Failed to load Team AI skill evaluations:', error);
          toast.error(t('teamAiSkills.errors.loadEvaluations'));
        }
      })
      .finally(() => {
        if (active) setEvaluationsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedVersion?.id, t]);

  const openSkillDialog = (mode: 'create' | 'import') => {
    setSkillDialogMode(mode);
    setSkillForm(EMPTY_SKILL_FORM);
    setSkillDialogOpen(true);
  };

  const submitSkill = async () => {
    const normalized: TeamAiSkillCreateInput = {
      key: skillForm.key.trim(),
      name: skillForm.name.trim(),
      description: skillForm.description.trim(),
      agent: skillForm.agent,
      category: skillForm.category,
      content: skillForm.content.trim(),
      executable: skillForm.executable || null,
    };
    if (
      normalized.key.length < 3 ||
      normalized.name.length < 3 ||
      normalized.description.length < 3 ||
      normalized.content.length === 0
    ) {
      toast.error(t('teamAiSkills.errors.requiredFields'));
      return;
    }
    setBusyAction('create-skill');
    try {
      await createTeamAiSkill(normalized, skillDialogMode === 'import');
      toast.success(
        skillDialogMode === 'import' ? 'Skill imported' : 'Skill created',
      );
      setSkillDialogOpen(false);
      await loadSkills();
    } catch (error) {
      console.error('Failed to save Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.saveSkill'));
    } finally {
      setBusyAction(null);
    }
  };

  const readImportFile = async (file: File) => {
    try {
      if (file.size > MAX_IMPORT_FILE_BYTES) {
        toast.error(t('teamAiSkills.errors.importTooLarge'));
        return;
      }
      const parsed: unknown = JSON.parse(await file.text());
      if (!isTeamAiSkillImport(parsed)) {
        toast.error(t('teamAiSkills.errors.importInvalid'));
        return;
      }
      setSkillForm(parsed);
    } catch (error) {
      console.error('Failed to import Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.importInvalid'));
    }
  };

  const openEditor = (mode: 'edit' | 'new') => {
    if (!selectedVersion) return;
    setEditorMode(mode);
    setContentDraft(selectedVersion.content);
    setEditorOpen(true);
  };

  const saveVersion = async () => {
    if (!selectedSkill || !selectedVersion || !contentDraft.trim()) return;
    const normalizedContent = contentDraft.trim();
    if (
      editorMode === 'edit' &&
      normalizedContent === selectedVersion.content
    ) {
      toast.info('No instruction changes to save.');
      setEditorOpen(false);
      return;
    }
    setBusyAction('save-version');
    try {
      const version =
        editorMode === 'edit'
          ? await updateTeamAiSkillDraft(selectedVersion.id, normalizedContent)
          : await createTeamAiSkillVersion(selectedSkill.id, normalizedContent);
      setSelectedVersionId(version.id);
      setEditorOpen(false);
      toast.success(
        editorMode === 'edit' ? 'Draft saved' : 'New draft created',
      );
      await loadSkills();
    } catch (error) {
      console.error('Failed to save Team AI skill version:', error);
      toast.error(t('teamAiSkills.errors.saveVersion'));
    } finally {
      setBusyAction(null);
    }
  };

  const evaluateVersion = async () => {
    if (!selectedVersion) return;
    setBusyAction('evaluate');
    try {
      const run = await evaluateTeamAiSkillVersion(
        selectedVersion.id,
        evaluationOutputTokens,
      );
      toast.success(
        run.state === 'passed'
          ? 'All evaluation cases passed'
          : `${run.totalCases - run.passedCases} evaluation case(s) failed`,
      );
      await loadSkills();
      const refreshedRuns = await listTeamAiSkillEvaluations(
        selectedVersion.id,
      );
      setEvaluations(refreshedRuns);
    } catch (error) {
      console.error('Failed to evaluate Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.evaluate'));
    } finally {
      setBusyAction(null);
    }
  };

  const publishVersion = async () => {
    if (!selectedVersion) return;
    setBusyAction('publish');
    try {
      await changeTeamAiSkillVersionState(selectedVersion.id, 'publish');
      toast.success(`Version ${selectedVersion.version} published`);
      await loadSkills();
    } catch (error) {
      console.error('Failed to publish Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.publish'));
    } finally {
      setBusyAction(null);
    }
  };

  const retireVersion = async () => {
    if (!retireTarget) return;
    setBusyAction('retire');
    try {
      await changeTeamAiSkillVersionState(retireTarget.id, 'retire');
      toast.success(`Version ${retireTarget.version} retired`);
      setRetireTarget(null);
      await loadSkills();
    } catch (error) {
      console.error('Failed to retire Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.retire'));
    } finally {
      setBusyAction(null);
    }
  };

  const createAssignment = async () => {
    if (!selectedVersion) return;
    const numericPriority = Number(priority);
    if (
      !Number.isInteger(numericPriority) ||
      numericPriority < 0 ||
      numericPriority > 1000
    ) {
      toast.error(t('teamAiSkills.errors.priority'));
      return;
    }
    if (scopeType !== 'platform' && !scopeKey.trim()) {
      toast.error(t('teamAiSkills.errors.scopeKey', { scope: scopeType }));
      return;
    }
    setBusyAction('assign');
    try {
      await assignTeamAiSkill({
        skillVersionId: selectedVersion.id,
        scopeType,
        scopeKey: scopeType === 'platform' ? null : scopeKey.trim(),
        priority: numericPriority,
      });
      toast.success(t('teamAiSkills.messages.assignmentSaved'));
      setAssignmentOpen(false);
      await loadSkills();
    } catch (error) {
      console.error('Failed to assign Team AI skill:', error);
      toast.error(t('teamAiSkills.errors.assign'));
    } finally {
      setBusyAction(null);
    }
  };

  const removeAssignment = async () => {
    if (!removeTarget) return;
    setBusyAction('remove-assignment');
    try {
      await removeTeamAiSkillAssignment(removeTarget.id);
      toast.success(t('teamAiSkills.messages.assignmentRemoved'));
      setRemoveTarget(null);
      await loadSkills();
    } catch (error) {
      console.error('Failed to remove Team AI skill assignment:', error);
      toast.error(t('teamAiSkills.errors.remove'));
    } finally {
      setBusyAction(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid min-h-[34rem] border lg:grid-cols-[19rem_minmax(0,1fr)]">
          <Skeleton className="h-full rounded-none border-r" />
          <Skeleton className="h-full rounded-none" />
        </div>
      </div>
    );
  }

  const currentEvaluationPassed =
    selectedVersion?.state === 'evaluated' &&
    evaluations.some(
      (run) =>
        run.id === selectedVersion.evaluationRunId && run.state === 'passed',
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h2 className="text-base font-semibold">Instruction skill catalog</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Refresh skill catalog"
            title="Refresh skill catalog"
            disabled={refreshing}
            onClick={() => void loadSkills(true)}
          >
            <RefreshCw className={cn(refreshing && 'animate-spin')} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openSkillDialog('import')}
          >
            <Upload />
            Import JSON
          </Button>
          <Button size="sm" onClick={() => openSkillDialog('create')}>
            <Plus />
            New skill
          </Button>
        </div>
      </div>

      <div className="grid min-h-[36rem] overflow-hidden border lg:grid-cols-[19rem_minmax(0,1fr)]">
        <SkillCatalogList
          skills={skills}
          selectedSkillId={selectedSkillId}
          onSelect={setSelectedSkillId}
        />

        {selectedSkill && selectedVersion ? (
          <div className="min-w-0 px-4 py-5 sm:px-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold">
                    {selectedSkill.name}
                  </h3>
                  <Badge variant="outline">{selectedSkill.category}</Badge>
                  <Badge
                    variant={skillStateBadgeVariant(selectedVersion.state)}
                  >
                    {selectedVersion.state}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedSkill.description}
                </p>
                <p className="mt-2 font-mono text-xs text-muted-foreground">
                  {selectedSkill.key} ·{' '}
                  {selectedVersion.contentHash.slice(0, 12)}
                </p>
              </div>
              <Select
                value={selectedVersion.id}
                onValueChange={setSelectedVersionId}
              >
                <SelectTrigger
                  className="w-44"
                  aria-label="Selected skill version"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedSkill.versions.map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      v{version.version} · {version.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 border-y py-3">
              {selectedVersion.state === 'draft' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditor('edit')}
                >
                  <Pencil />
                  Edit draft
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditor('new')}
              >
                <History />
                New version
              </Button>
              {['draft', 'evaluated'].includes(selectedVersion.state) && (
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Evaluation output token limit"
                    className="h-9 w-24"
                    type="number"
                    min={512}
                    max={10_000}
                    step={128}
                    value={evaluationOutputTokens}
                    onChange={(event) =>
                      setEvaluationOutputTokens(
                        Math.max(
                          512,
                          Math.min(10_000, Number(event.target.value) || 512),
                        ),
                      )
                    }
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyAction === 'evaluate'}
                    onClick={() => void evaluateVersion()}
                  >
                    {busyAction === 'evaluate' ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Beaker />
                    )}
                    Run evaluation
                  </Button>
                </div>
              )}
              {selectedVersion.state === 'evaluated' && (
                <Button
                  size="sm"
                  disabled={
                    !currentEvaluationPassed || busyAction === 'publish'
                  }
                  onClick={() => void publishVersion()}
                >
                  <Rocket />
                  Publish
                </Button>
              )}
              {selectedVersion.state === 'published' && (
                <>
                  <Button size="sm" onClick={() => setAssignmentOpen(true)}>
                    <Layers3 />
                    Assign scope
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRetireTarget(selectedVersion)}
                  >
                    <Archive />
                    Retire
                  </Button>
                </>
              )}
            </div>

            <section className="mt-5" aria-labelledby="skill-content-heading">
              <div className="mb-2 flex items-center justify-between">
                <h4
                  id="skill-content-heading"
                  className="text-sm font-semibold"
                >
                  Version instructions
                </h4>
                <span className="text-xs text-muted-foreground">
                  {selectedVersion.content.length.toLocaleString()} characters
                </span>
              </div>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap border bg-muted/40 p-4 font-mono text-xs leading-5">
                {selectedVersion.content}
              </pre>
            </section>

            <section className="mt-6" aria-labelledby="assignments-heading">
              <div className="mb-2 flex items-center justify-between">
                <h4 id="assignments-heading" className="text-sm font-semibold">
                  Active assignments
                </h4>
                <span className="text-xs text-muted-foreground">
                  {
                    selectedVersion.assignments.filter((item) => item.enabled)
                      .length
                  }{' '}
                  active
                </span>
              </div>
              {selectedVersion.assignments.length === 0 ? (
                <div className="border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  This version is not assigned to any scope.
                </div>
              ) : (
                <div className="overflow-x-auto border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scope</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedVersion.assignments.map((assignment) => (
                        <TableRow key={assignment.id}>
                          <TableCell className="text-sm">
                            {describeSkillScope(
                              assignment.scopeType,
                              assignment.scopeKey,
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {assignment.priority}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                assignment.enabled ? 'success' : 'stopped'
                              }
                            >
                              {assignment.enabled ? 'active' : 'disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Remove assignment"
                              title="Remove assignment"
                              onClick={() => setRemoveTarget(assignment)}
                            >
                              <Trash2 />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>

            <section className="mt-6" aria-labelledby="evaluation-heading">
              <div className="mb-2 flex items-center justify-between">
                <h4 id="evaluation-heading" className="text-sm font-semibold">
                  Evaluation evidence
                </h4>
                <span className="text-xs text-muted-foreground">
                  Latest 20 runs
                </span>
              </div>
              <EvaluationEvidence
                runs={evaluations}
                loading={evaluationsLoading}
              />
            </section>
          </div>
        ) : (
          <div className="flex min-h-80 items-center justify-center p-8 text-center text-sm text-muted-foreground">
            Select a skill to inspect its versions and evidence.
          </div>
        )}
      </div>

      <SkillDraftDialog
        open={skillDialogOpen}
        mode={skillDialogMode}
        form={skillForm}
        busy={busyAction === 'create-skill'}
        onOpenChange={setSkillDialogOpen}
        onFormChange={setSkillForm}
        onImportFile={(file) => void readImportFile(file)}
        onSubmit={() => void submitSkill()}
      />

      <SkillVersionDialog
        open={editorOpen}
        mode={editorMode}
        content={contentDraft}
        busy={busyAction === 'save-version'}
        onOpenChange={setEditorOpen}
        onContentChange={setContentDraft}
        onSubmit={() => void saveVersion()}
      />

      <SkillAssignmentDialog
        open={assignmentOpen}
        scopeType={scopeType}
        scopeKey={scopeKey}
        priority={priority}
        busy={busyAction === 'assign'}
        onOpenChange={setAssignmentOpen}
        onScopeTypeChange={(scope) => {
          setScopeType(scope);
          setScopeKey('');
        }}
        onScopeKeyChange={setScopeKey}
        onPriorityChange={setPriority}
        onSubmit={() => void createAssignment()}
      />

      <ConfirmDialog
        open={Boolean(retireTarget)}
        onOpenChange={(open) => !open && setRetireTarget(null)}
        title="Retire this skill version?"
        description="Retirement is permanent and disables every assignment that points to this version. Create or assign another published version first when continuity is required."
        confirmLabel="Retire version"
        loading={busyAction === 'retire'}
        onConfirm={() => void retireVersion()}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this assignment?"
        description="The version stays published, but this scope will stop receiving the skill after removal."
        confirmLabel="Remove assignment"
        loading={busyAction === 'remove-assignment'}
        onConfirm={() => void removeAssignment()}
      />
    </div>
  );
}
